import { useState } from "react";
import ModalWrapper from "../../components/Modal/ModalWrapper";
import CustomButton from "../../components/CustomButton";
import DatePicker from "../../components/misc/DatePicker";
import TextInput from "../../components/misc/TextInput";
import {
  dateToString,
  getDateFromJulianDay,
  getDaysInYear,
  getJulianDateInfo,
} from "../../utils/dateAndTime";

const minDate = getDateFromJulianDay(1, 0)!;
const maxDate = getDateFromJulianDay(9999, 364)!;

function JulianDateConverterModal({ onClose }: { onClose: () => void }) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [value, setValue] = useState(() => {
    const today = getJulianDateInfo(new Date())!;
    return { year: String(today.year), day: today.formattedDay };
  });
  const daysInYear = /^\d{1,4}$/.test(value.year)
    ? getDaysInYear(Number(value.year))
    : null;
  const yearError = !daysInYear ? "Introdu un an între 1 și 9999." : undefined;
  const dayError = !/^\d{1,3}$/.test(value.day)
    ? "Introdu ziua iuliană, de la 000."
    : daysInYear && Number(value.day) >= daysInYear
    ? `Pentru anul ${Number(value.year)}, ziua trebuie să fie între 000 și ${
        daysInYear - 1
      }.`
    : undefined;
  const date =
    !yearError && !dayError
      ? getDateFromJulianDay(Number(value.year), Number(value.day))
      : null;
  const julianDate = getJulianDateInfo(date);

  const handleDateChange = (nextDate: Date | null) => {
    const next = getJulianDateInfo(nextDate);
    if (next) {
      setValue({ year: String(next.year), day: next.formattedDay });
    } else {
      setValue((previous) => ({ ...previous, day: "" }));
    }
  };

  return (
    <ModalWrapper
      show
      disableEscapeKeyDown={calendarOpen}
      handleClose={onClose}
      title="Convertor dată ↔ Julian"
      modalSize="sm"
      actionsComponent={<CustomButton onClick={onClose}>Închide</CustomButton>}>
      <p className="mb-6 mt-0 text-sm leading-relaxed text-muted-dark">
        Alege data calendaristică sau introdu anul și ziua iuliană. Conversia se
        face automat. În aplicație, 1 ianuarie este ziua 000.
      </p>
      <div className="grid items-start gap-5 sm:grid-cols-2">
        <DatePicker
          label="Data gregoriană"
          placeholder="zz/ll/aaaa"
          dateFormat="DD/MM/YYYY"
          views={["day"]}
          onOpen={() => setCalendarOpen(true)}
          onClose={() => setCalendarOpen(false)}
          value={date}
          handleChangeDate={handleDateChange}
          minDate={minDate}
          maxDate={maxDate}
          gutterBottom={false}
          textInputClassName="!mb-0"
        />
        <div className="grid grid-cols-2 gap-3">
          <TextInput
            name="julian-year"
            label="Anul"
            value={value.year}
            onChange={(year) =>
              setValue((previous) => ({
                ...previous,
                year: String(year ?? ""),
              }))
            }
            inputProps={{ inputMode: "numeric", maxLength: 4 }}
            noBottomSpacing
            touched
            error={yearError}
          />
          <TextInput
            name="julian-day"
            label="Ziua iuliană"
            placeholder="000–365"
            value={value.day}
            onChange={(day) =>
              setValue((previous) => ({ ...previous, day: String(day ?? "") }))
            }
            inputProps={{ inputMode: "numeric", maxLength: 3 }}
            noBottomSpacing
            touched
            error={dayError}
          />
        </div>
      </div>
      <div className="mt-6 rounded-control bg-accent-subtle p-4">
        <p className="mb-2 mt-0 text-sm font-medium text-muted-dark">
          Corespondență
        </p>
        <output
          className="block text-lg font-semibold text-slate-800"
          aria-live="polite">
          {date && julianDate
            ? `${dateToString(date)} ↔ ${julianDate.formattedDay} / ${
                julianDate.year
              }`
            : "Completează un an și o zi valide."}
        </output>
        {julianDate && (
          <p className="mb-0 mt-2 text-sm text-muted-dark">
            Cod Julian (AAZZZ):{" "}
            <strong className="font-mono text-[#2196f3]">
              {julianDate.code}
            </strong>
          </p>
        )}
      </div>
    </ModalWrapper>
  );
}

export default JulianDateConverterModal;
